/**
=========================================================
* Soft UI Dashboard React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Soft UI Dashboard React Base Styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import boxShadows from "assets/theme/base/boxShadows";

// Soft UI Dashboard React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";

const { white, text, info, primary, dark } = colors;
const { size } = typography;
const { buttonBoxShadow } = boxShadows;

const contained = {
  base: {
    backgroundColor: white.main,
    minHeight: pxToRem(40),
    color: text.main,
    boxShadow: buttonBoxShadow.main,
    padding: `${pxToRem(12)} ${pxToRem(24)}`,

    "&:hover": {
      backgroundColor: white.main,
      boxShadow: buttonBoxShadow.stateOf,
    },

    "&:focus": {
      boxShadow: buttonBoxShadow.stateOf,
    },

    "&:active, &:active:focus, &:active:hover": {
      opacity: 0.85,
      boxShadow: buttonBoxShadow.stateOf,
    },

    "&:disabled": {
      boxShadow: buttonBoxShadow.main,
    },

    "& .material-icon, .material-icons-round, svg": {
      fontSize: `${pxToRem(16)} !important`,
    },
  },

  small: {
    minHeight: pxToRem(32),
    padding: `${pxToRem(8)} ${pxToRem(32)}`,
    fontSize: size.xs,

    "& .material-icon, .material-icons-round, svg": {
      fontSize: `${pxToRem(12)} !important`,
    },
  },

  large: {
    minHeight: pxToRem(47),
    padding: `${pxToRem(14)} ${pxToRem(64)}`,
    fontSize: size.sm,

    "& .material-icon, .material-icons-round, svg": {
      fontSize: `${pxToRem(22)} !important`,
    },
  },

  /**
   * The primary button is the gold accent, not navy.
   *
   * The stock theme hard-wired this to `info`, so every primary action came
   * out navy while chips, tabs and the sidebar used the real primary - which
   * is why nothing looked deliberate.
   *
   * Text is navy, not white: white on #C9A227 is about 2.3:1 contrast and
   * fails WCAG outright, while navy on gold is ~7:1 and is the pairing that
   * makes gold read as expensive rather than as a warning colour.
   */
  primary: {
    backgroundColor: primary.main,
    color: dark.main,

    "&:hover": {
      backgroundColor: primary.focus,
      color: dark.main,
    },

    "&:focus:not(:hover)": {
      backgroundColor: primary.focus,
      color: dark.main,
      boxShadow: buttonBoxShadow.stateOfNotHover,
    },
  },

  // navy - for the second-most-important action on a screen
  secondary: {
    backgroundColor: dark.main,
    color: white.main,

    "&:hover": {
      backgroundColor: dark.focus,
      color: white.main,
    },

    "&:focus:not(:hover)": {
      backgroundColor: dark.focus,
      color: white.main,
      boxShadow: buttonBoxShadow.stateOfNotHover,
    },
  },

  info: {
    backgroundColor: info.main,
    color: white.main,

    "&:hover": {
      backgroundColor: info.focus,
      color: white.main,
    },

    "&:focus:not(:hover)": {
      backgroundColor: info.focus,
      boxShadow: buttonBoxShadow.stateOfNotHover,
    },
  },
};

export default contained;
